/**
 * Даты в портале показываются и вводятся в часовом поясе приложения (Asia/Almaty),
 * независимо от настроек ОС пользователя. Сервер хранит UTC (timestamptz).
 */
export const APP_TZ = "Asia/Almaty";

const dateTime = new Intl.DateTimeFormat("ru-RU", {
  timeZone: APP_TZ,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const timeOnly = new Intl.DateTimeFormat("ru-RU", { timeZone: APP_TZ, hour: "2-digit", minute: "2-digit" });

export const fmtDateTime = (iso: string | Date | null | undefined): string => (iso ? dateTime.format(new Date(iso)) : "—");
export const fmtTime = (iso: string | Date): string => timeOnly.format(new Date(iso));

/** Части «настенного» времени в поясе tz для момента date. */
function wallParts(date: Date, tz: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return { y: get("year"), mo: get("month"), d: get("day"), h: get("hour"), mi: get("minute"), s: get("second") };
}

/** ISO (UTC) → значение для <input type="datetime-local"> в поясе приложения: "2026-10-18T14:30". */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const p = wallParts(new Date(iso), APP_TZ);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

/** Значение datetime-local (настенное время в поясе приложения) → ISO в UTC. Пустая/битая строка → null. */
export function localInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  // Берём момент, как если бы это был UTC, смотрим, какое настенное время он даёт в tz, и сдвигаем на разницу.
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const p = wallParts(new Date(guess), APP_TZ);
  const asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  const offsetMs = asUtc - guess;
  return new Date(guess - offsetMs).toISOString();
}

/** Оставшееся время до момента: «2 ч 05 мин», «14 мин 30 с», «срок истёк». */
export function timeLeft(iso: string, now = Date.now()): string {
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return "срок истёк";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  if (h >= 24) return `${Math.floor(h / 24)} дн ${h % 24} ч`;
  if (h > 0) return `${h} ч ${String(m).padStart(2, "0")} мин`;
  return `${m} мин ${String(s).padStart(2, "0")} с`;
}
